import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { BarChartOutlined, Output } from "@mui/icons-material";

import { useTheme } from "@mui/material/styles";

import { MindsetSkeleton } from "./MindsetSkeleton";
import { useActivePage } from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { SegmentPanel } from "@/components/molecules";
import { useAudienceDispatch } from "@/context";
import { useAudience } from "@/hooks";
import { AudienceSegment, Breadcrumb } from "@/types";
import { getAudienceBreadcrumb, toPercentage, getSegmentPaletteColor } from "@/utils";


const MindsetPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAudienceDispatch();
  const theme = useTheme();

  let { audienceId } = useParams<{ audienceId: string }>();
  const {
    audience,
    loading: audienceLoading,
    error: audienceError,
  } = useAudience(audienceId, "mindset");

  const calcPercentage = (sizes: number[] | undefined, idx: number) => {
    if (!sizes) return '';
    const total = sizes.reduce((a, b) => a + b, 0);
    return toPercentage(sizes[idx] / total, 1);
  }

  if (audienceError) {
    return (
      <PageContainer
        errors={{ AudienceNotFound: audienceError }}
        loading={audienceLoading}
        loadingText={"Loading mindset data"}
      >
        <Grid container spacing={2}>
        </Grid>
      </PageContainer>
    );
  }

  const handleAudienceInsight = () => {
    navigate("insight");
  };

  const handleAudienceExport = () => {
    navigate("export");
  };

  const handleSegmentSelect = (segment: AudienceSegment) => {
    dispatch({ type: 'CHANGE_SEGMENT', payload: segment });
    navigate("insight");
  }

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<BarChartOutlined />}
        loading={audienceLoading}
        onClick={handleAudienceInsight}
      >
        Insight
      </Button>
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<Output />}
        disabled={audienceLoading}
        onClick={handleAudienceExport}
      >
        Export
      </Button>
    </Stack>
  );

  return (
    <PageContainer
      errors={{ AudienceNotFound: audienceError }}
      loading={audienceLoading}
      loadingText={"Loading mindset data"}
      actions={actionButtons}
    >
      {audienceLoading ? (
        <MindsetSkeleton />
      ): (
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {audience && (
          <>
            {/* Mindset Name */}
            <Grid size={12} mb={1}>
              <Typography variant="h6">
                {audience.audience_name}
              </Typography>
            </Grid>

            {/* Segments */}
            {audience.segments.map((segment, index) => (
              <Grid size={12} key={index}>
                <SegmentPanel
                  segment={segment}
                  segmentValue={calcPercentage(audience.audience_size?.map(x => x.num_postcodes || 0), index)}
                  segmentSize={audience.audience_size && audience.audience_size[index]}
                  description={audience.summary ? audience.summary[index]?.description_short : ''}
                  align="left"
                  color={getSegmentPaletteColor(theme, index, audience.segments.length)}
                  onSelect={handleSegmentSelect}
                />
              </Grid>
            ))}
          </>
        )}
      </Grid>
      )}
    </PageContainer>
  );
};

export default MindsetPage;
